import { useEffect, useRef } from 'react';
import type { WebappResponse } from '@eventbrite/event-listing-service-client';
import { Stage } from '@eventbrite/webapp-constants';
import {
  buildEventAttributes,
  buildEventProduct,
  buildPageViewAttributes,
  getRoktConfig,
  getRoktConsent,
  initRokt,
  logPageView,
  logProductView,
  setRoktJurisdiction,
} from '@eventbrite/rokt-sdk';

type UseRoktPdpViewEffectArgs = {
  pageContext: WebappResponse | null | undefined;
  stage?: string;
  isPreview?: boolean;
  apiKey?: string | null;
  jurisdiction?: 'EU' | 'ROW' | null;
  isConsentInitialized: boolean;
};

let sdkInited = false;
let sdkConsent: ReturnType<typeof getRoktConsent> | undefined;
const AIRGAP_REGISTRATION_INTERVAL_MS = 100;
const AIRGAP_REGISTRATION_TIMEOUT_MS = 5_000;

export function useRoktPdpViewEffect({
  pageContext,
  stage,
  isPreview,
  apiKey,
  jurisdiction,
  isConsentInitialized,
}: UseRoktPdpViewEffectArgs) {
  const trackedEventIdRef = useRef<string | null>(null);
  const isReloadingRef = useRef(false);

  useEffect(() => {
    if (jurisdiction) {
      setRoktJurisdiction(jurisdiction);
    }
  }, [jurisdiction]);

  useEffect(() => {
    if (isPreview || !apiKey || !isConsentInitialized) {
      return;
    }

    let registeredAirgap: typeof window.airgap | undefined;
    const handleConsentChange = () => {
      if (!isReloadingRef.current) {
        isReloadingRef.current = true;
        window.location.reload();
      }
    };
    const register = () => {
      const airgap = window.airgap;
      if (!airgap?.addEventListener) {
        return false;
      }

      registeredAirgap = airgap;
      airgap.addEventListener('consent-change', handleConsentChange);
      return true;
    };

    if (register()) {
      return () => registeredAirgap?.removeEventListener?.('consent-change', handleConsentChange);
    }

    const interval = window.setInterval(() => {
      if (register()) {
        window.clearInterval(interval);
        window.clearTimeout(timeout);
      }
    }, AIRGAP_REGISTRATION_INTERVAL_MS);
    const timeout = window.setTimeout(() => window.clearInterval(interval), AIRGAP_REGISTRATION_TIMEOUT_MS);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
      registeredAirgap?.removeEventListener?.('consent-change', handleConsentChange);
    };
  }, [isPreview, apiKey, isConsentInitialized]);

  useEffect(() => {
    if (isPreview || !apiKey || !isConsentInitialized) {
      return;
    }

    const consent = getRoktConsent();
    if (sdkInited) {
      const consentChanged =
        sdkConsent?.block !== consent.block ||
        sdkConsent?.noFunctional !== consent.noFunctional ||
        sdkConsent?.noTargeting !== consent.noTargeting;

      if (consentChanged && !isReloadingRef.current) {
        isReloadingRef.current = true;
        window.location.reload();
      }
      return;
    }

    if (consent.block) {
      return;
    }

    initRokt(getRoktConfig(stage === Stage.PROD, apiKey ?? undefined), {
      noFunctional: consent.noFunctional,
      noTargeting: consent.noTargeting,
    });
    sdkInited = true;
    sdkConsent = consent;
  }, [stage, isPreview, apiKey, isConsentInitialized]);

  useEffect(() => {
    const basicInfo = pageContext?.basicInfo;
    const eventId = basicInfo?.id;

    if (
      isPreview ||
      !eventId ||
      !apiKey ||
      !isConsentInitialized ||
      isReloadingRef.current ||
      !sdkInited ||
      trackedEventIdRef.current === eventId
    ) {
      return;
    }

    const consent = getRoktConsent();
    if (consent.block) {
      return;
    }

    trackedEventIdRef.current = eventId;

    logPageView('event_pdp', buildPageViewAttributes({ pageName: 'event_pdp' }));

    const input = {
      eventId,
      name: basicInfo?.name ?? '',
      currency: basicInfo?.currency,
    };
    logProductView([buildEventProduct(input)], buildEventAttributes(input));
  }, [pageContext, isPreview, apiKey, isConsentInitialized]);
}
